<template>
  <section class="invoice-preview-wrapper">
    <DeleteModal
      class="display-none-print"
      modal-id="modal-delete"
      @deleteToggle="deleteItem"
    />
    <DeleteModal
      class="display-none-print"
      modal-id="radiology-delete"
      @deleteToggle="deleteRadiologyItem"
    />

    <b-modal
      id="modal-create-radiology"
      ok-only
      centered
      size="lg"
      no-close-on-backdrop
      :title="`${$t('radiology.creat-radiology')}`"
    >
      <template #modal-footer="{}">
        <b-button
          ref="button"
          class="m-0"
          :disabled="loading"
          block
          variant="primary"
          @click="createRadiology"
        >
          <span v-if="!loading">{{ $t("common.submit") }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>
      <b-row>
        <b-col cols="12" md="12">
          <AutoComplete
            v-model="createRadiologyForm.radiology_id"
            :label="$t('radiology.radiology')"
            :placeholder="$t('radiology.radiology')"
            param-query="name"
            :serializer="(val) => val.id"
            endpoint="radiology"
            text-title="name"
            keyword="patient"
          />
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="modal-upload-attachment"
      :title="$t('common.upload-attachment')"
      ok-only
      size="lg"
      @hidden="resetUploadState"
    >
      <template #modal-footer="{}">
        <b-button
          ref="button"
          class="m-0"
          :disabled="loading || !selectedFile"
          block
          variant="primary"
          @click="submitAttachment"
        >
          <feather-icon v-if="!loading" class="mr-1" icon="UploadIcon" />
          <span v-if="!loading">{{ $t("common.upload") }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>

      <div class="upload-section">
        <div class="upload-area">
          <b-form-file
            v-model="selectedFile"
            accept=".jpg,.jpeg,.png,.pdf"
            :placeholder="$t('common.select-file')"
            class="upload-input"
            @change="previewFile"
          />
          <small class="text-muted d-block mt-2">
            <feather-icon class="mr-1" icon="InfoIcon" />
            {{ $t("common.supported-formats") }}: JPG, PNG, PDF (Max 10MB)
          </small>
        </div>

        <div v-if="filePreview" class="preview-section mt-4">
          <h6 class="preview-title">
            {{ $t("common.preview") }}
          </h6>
          <div class="preview-container">
            <img
              v-if="isImage"
              :src="filePreview"
              alt="Image Preview"
              class="preview-image"
            />
            <div v-else class="preview-pdf">
              <embed
                :src="filePreview"
                type="application/pdf"
                class="preview-embed"
              />
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Patient Information Card -->
    <b-card class="patient-info-card mb-4">
      <div class="patient-info-header">
        <div class="d-flex align-items-center">
          <div class="patient-avatar">
            <b-avatar size="60px" variant="primary" class="mr-3">
              <span class="text-white font-weight-bold">
                {{
                  getNestedProperty(patientCardsData, "patient.name", "")
                    .charAt(0)
                    .toUpperCase()
                }}
              </span>
            </b-avatar>
          </div>
          <div>
            <h3 class="patient-name mb-1">
              {{ getNestedProperty(patientCardsData, "patient.name") }}
            </h3>
            <p class="patient-id text-muted mb-0">
              {{ $t("reception.card-type") }}: {{ patientCardsData.type }}
            </p>
          </div>
        </div>
        <div class="patient-status">
          <b-badge class="status-badge" variant="success">
            {{ $t("common.active") }}
          </b-badge>
        </div>
      </div>

      <hr class="my-4" />
      <div class="patient-details-grid">
        <!-- Contact Information -->
        <div class="detail-section">
          <h6 class="section-title">
            {{ $t("common.contact-info") }}
          </h6>
          <div class="detail-item">
            <span class="detail-label"
              >{{ $t("reception.companion-name") }}:</span
            >
            <span class="detail-value">{{
              patientCardsData.full_companion_name || ""
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"
              >{{ $t("reception.companion-phone") }}:</span
            >
            <span class="detail-value">{{
              patientCardsData.companion_phone || ""
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"
              >{{ $t("reception.next-kin-name") }}:</span
            >
            <span class="detail-value">{{
              patientCardsData.next_of_kin_name || ""
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"
              >{{ $t("reception.next-kin-phone") }}:</span
            >
            <span class="detail-value">{{
              patientCardsData.next_of_kin_phone_number || ""
            }}</span>
          </div>
        </div>

        <!-- Medical Information -->
        <div class="detail-section">
          <h6 class="section-title">
            {{ $t("common.medical-info") }}
          </h6>
          <div class="detail-item">
            <span class="detail-label">{{ $t("reception.weight") }}:</span>
            <span class="detail-value">{{
              patientCardsData.weight || ""
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t("reception.height") }}:</span>
            <span class="detail-value">{{
              patientCardsData.height || ""
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t("reception.room") }}:</span>
            <span class="detail-value">{{ patientCardsData.room || "" }}</span>
          </div>
        </div>

        <!-- Health Conditions -->
        <div class="detail-section">
          <h6 class="section-title">
            {{ $t("common.health-conditions") }}
          </h6>
          <div class="condition-grid">
            <div class="condition-item">
              <span class="condition-label"
                >{{ $t("reception.smoking") }}:</span
              >
              <b-badge
                :variant="patientCardsData.is_smoker ? 'danger' : 'success'"
              >
                {{
                  patientCardsData.is_smoker
                    ? $t("common.yes")
                    : $t("common.no")
                }}
              </b-badge>
            </div>
            <div class="condition-item">
              <span class="condition-label"
                >{{ $t("reception.diabetic") }}:</span
              >
              <b-badge
                :variant="patientCardsData.has_diabetes ? 'warning' : 'success'"
              >
                {{
                  patientCardsData.has_diabetes
                    ? $t("common.yes")
                    : $t("common.no")
                }}
              </b-badge>
            </div>
            <div class="condition-item">
              <span class="condition-label"
                >{{ $t("reception.heart-disease") }}:</span
              >
              <b-badge
                :variant="
                  patientCardsData.has_heart_disease ? 'danger' : 'success'
                "
              >
                {{
                  patientCardsData.has_heart_disease
                    ? $t("common.yes")
                    : $t("common.no")
                }}
              </b-badge>
            </div>
            <div class="condition-item">
              <span class="condition-label"
                >{{ $t("reception.hypertension") }}:</span
              >
              <b-badge
                :variant="
                  patientCardsData.has_high_blood_pressure
                    ? 'warning'
                    : 'success'
                "
              >
                {{
                  patientCardsData.has_high_blood_pressure
                    ? $t("common.yes")
                    : $t("common.no")
                }}
              </b-badge>
            </div>
          </div>
          <div v-if="patientCardsData.allergy" class="detail-item mt-3">
            <span class="detail-label text-danger"
              >{{ $t("reception.allergy") }}:</span
            >
            <span class="detail-value text-danger">{{
              patientCardsData.allergy
            }}</span>
          </div>
        </div>

        <!-- Medical Details -->
        <div class="detail-section full-width">
          <h6 class="section-title">
            {{ $t("common.medical-details") }}
          </h6>
          <div class="medical-details-grid">
            <div class="medical-item">
              <span class="medical-label"
                >{{ $t("reception.patient-case") }}:</span
              >
              <span class="medical-value">{{
                patientCardsData.patient_case || ""
              }}</span>
            </div>
            <div class="medical-item">
              <span class="medical-label"
                >{{ $t("reception.diagnosis") }}:</span
              >
              <span class="medical-value">{{
                patientCardsData.diagnosis || ""
              }}</span>
            </div>
            <div class="medical-item">
              <span class="medical-label"
                >{{ $t("reception.investigation") }}:</span
              >
              <span class="medical-value">{{
                patientCardsData.investigation || ""
              }}</span>
            </div>
            <div class="medical-item">
              <span class="medical-label"
                >{{ $t("reception.treatment") }}:</span
              >
              <span class="medical-value">{{
                patientCardsData.treatment || ""
              }}</span>
            </div>
            <div class="medical-item">
              <span class="medical-label"
                >{{ $t("reception.examination") }}:</span
              >
              <span class="medical-value">{{
                patientCardsData.examination || ""
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Radiology Attachments Section -->
    <b-card class="radiology-attachments-card">
      <div class="radiology-header">
        <div class="d-flex align-items-center">
          <div class="radiology-icon">
            <feather-icon icon="CameraIcon" class="mr-2" />
          </div>
          <div>
            <h3 class="radiology-title mb-1">
              {{ $t("radiology.create-patient-radiology-attachments") }}
            </h3>
            <p class="radiology-subtitle text-muted mb-0">
              {{ $t("radiology.manage-attachments") }}
            </p>
          </div>
        </div>
        <b-button
          variant="primary"
          class="add-radiology-btn"
          @click="openModalCreateRadiology()"
        >
          <feather-icon icon="PlusIcon" class="mr-1" />
          {{ $t("radiology.creat-radiology") }}
        </b-button>
      </div>

      <div v-if="radiologyPatientData.length > 0" class="radiology-grid">
        <b-card
          v-for="radiology in radiologyPatientData"
          :key="radiology.id"
          class="radiology-item-card"
        >
          <div class="radiology-item-header">
            <div class="radiology-item-info">
              <h5 class="radiology-item-title">
                {{ getRadiologyName(radiology.radiology_id) }}
              </h5>
              <span class="radiology-item-count">
                {{ radiology.attachments ? radiology.attachments.length : 0 }}
                {{ $t("common.attachments") }}
              </span>
            </div>
            <div class="radiology-item-actions">
              <b-button
                variant="outline-success"
                size="sm"
                class="mr-2"
                @click="openUploadModal(radiology)"
              >
                <feather-icon icon="UploadIcon" class="mr-1" />
                {{ $t("common.add") }}
              </b-button>
              <b-button
                variant="outline-danger"
                size="sm"
                @click="deleteRadiology(radiology)"
              >
                <feather-icon icon="TrashIcon" class="mr-1" />
                {{ $t("common.delete") }}
              </b-button>
            </div>
          </div>

          <div
            v-if="radiology.attachments && radiology.attachments.length"
            class="attachments-container"
          >
            <div
              v-for="attachment in radiology.attachments"
              :key="attachment.id"
              class="attachment-item"
            >
              <div class="attachment-preview">
                <div
                  v-if="!attachment.attachment_url.includes('pdf')"
                  class="attachment-image-container"
                >
                  <img
                    :src="attachment.attachment_url"
                    alt="attachment"
                    class="attachment-image"
                  />
                  <div class="attachment-overlay">
                    <feather-icon icon="EyeIcon" class="overlay-icon" />
                  </div>
                </div>
                <div v-else class="attachment-pdf-container">
                  <div class="pdf-icon">
                    <feather-icon icon="FileTextIcon" />
                  </div>
                  <div class="pdf-label">
                    {{ $t("common.pdf-file") }}
                  </div>
                </div>
              </div>

              <div class="attachment-actions">
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-2"
                  @click="openPdf(attachment.attachment_url)"
                >
                  <feather-icon icon="EyeIcon" class="mr-1" />
                  {{ $t("common.show") }}
                </b-button>
                <b-button
                  variant="danger"
                  size="sm"
                  @click="deleteModel(attachment)"
                >
                  <feather-icon icon="TrashIcon" class="mr-1" />
                  {{ $t("common.delete") }}
                </b-button>
              </div>
            </div>
          </div>

          <div v-else class="no-attachments">
            <div class="no-attachments-content">
              <feather-icon icon="ImageIcon" class="no-attachments-icon" />
              <p class="no-attachments-text">
                {{ $t("common.no-attachments") }}
              </p>
            </div>
          </div>
        </b-card>
      </div>

      <div v-else class="empty-radiology-state">
        <div class="empty-state-content">
          <div class="empty-state-icon">
            <feather-icon icon="CameraIcon" />
          </div>
          <h4 class="empty-state-title">
            {{ $t("radiology.no-radiology") }}
          </h4>
          <p class="empty-state-description">
            {{ $t("radiology.no-radiology-description") }}
          </p>
          <b-button
            variant="primary"
            class="empty-state-action"
            @click="openModalCreateRadiology()"
          >
            <feather-icon icon="PlusIcon" class="mr-1" />
            {{ $t("radiology.creat-radiology") }}
          </b-button>
        </div>
      </div>
    </b-card>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BAvatar,
  BBadge,
  BSpinner,
  BModal,
  BFormFile,
} from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import axios from "axios";
import formatPhoneNumber from "@/utils/formatPhoneNumber";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";

const AutoComplete = () => import("@/components/common/AutoComplete.vue");
export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BAvatar,
    BBadge,
    BSpinner,
    BModal,
    DeleteModal,
    BFormFile,
    FeatherIcon,
    AutoComplete,
    ToastificationContent,
  },
  data() {
    return {
      patientCardsData: [],
      page: 1,
      totalRows: "",
      todayDate: "",
      take: 10,
      loading: false,
      selected: "",
      option: [],
      cardId: "",
      createRadiologyForm: {
        patient_card_id: this.$route.params.cardId,
        type: "0",
        radiology_id: "",
      },
      params: {
        form: {},
        path: "",
      },
      radiologyPatientData: [],
      radiologyData: [],
      deleteForm: {},
      selectedFile: null,
      filePreview: null,
      isImage: false,
      attachmentForm: {},
    };
  },

  mounted() {
    this.cardId = this.$route.params.cardId;
    console.log(this.cardId);
    this.getPatientCards();
    this.getPatientRadiology();
    this.getRadiology();
  },
  methods: {
    getNestedProperty(obj, path, defaultValue = "N/A") {
      const result = path
        .split(".")
        .reduce(
          (acc, part) => (acc && acc[part] ? acc[part] : defaultValue),
          obj
        );
      return result;
    },
    openModalCreateRadiology() {
      this.$bvModal.show("modal-create-radiology");
    },
    // this is temporary
    formatPhoneNumber,
    async getPatientCards() {
      this.loading = true;
      let url = `/patientCard?include=patient&page=${this.page}&take=${this.take}&sort=-id&filter[is_active]=true&filter[id]=${this.cardId}`;

      if (this.patientName) {
        console.log(this.patientName);
        url += `&filter[patient.name]=${this.patientName}`;
      }

      await this.getList(url);
      Object.keys(this.all_data).forEach((item) => {
        if (this.all_data[item] == null) this.all_data[item] = "";
      });
      this.dataCount = this.all_data.total;
      this.patientCardsData = this.all_data.data[0];
      console.log(this.patientCardsData);
      this.loading = false;
    },
    async getPatientRadiology() {
      await this.getList(
        `patientCardRadiology?take=20&filter[patient_card_id]=${this.cardId}&include=attachments`
      );
      this.radiologyPatientData = this.all_data.data;
      console.log(this.radiologyPatientData);
    },
    async createRadiology() {
      console.log(this.createRadiologyForm);
      this.loading = true;

      this.params.form = this.createRadiologyForm;
      this.params.path = "/patientCardRadiology";
      this.$store
        .dispatch("create", this.params)
        .then(async () => {
          await this.getPatientRadiology();
          this.$bvModal.hide("modal-create-radiology");
          this.loading = false;
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "success",
              variant: "success",
              text: "Radiology created successfully",
            },
          });
        })
        .catch((error) => {
          this.loading = false;
          if (error.response.status === 400) {
            Object.keys(error.response.data).forEach((item) => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: "validation error",
                  variant: "danger",
                  text: error.response.data[item][0],
                },
              });
            });
          }
        });
    },
    async getRadiology() {
      await this.getList("radiology");
      this.radiologyData = this.all_data.data;
      console.log(this.radiologyData);
    },
    getRadiologyName(radiologyId) {
      for (const radiology of this.radiologyData) {
        if (radiology.id === radiologyId) {
          return radiology.name;
        }
      }
      return "N/A";
    },
    openPdf(url) {
      window.open(url, "_blank");
    },
    deleteModel(item) {
      this.deleteForm = item;
      console.log("deleteForm", this.deleteForm);
      this.$bvModal.show("modal-delete");
    },
    deleteItem() {
      this.$store
        .dispatch("delete", `/attachment/${this.deleteForm.id}`)
        .then(() => {
          this.getPatientRadiology();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: `deleted ${this.deleteForm.attachment_url} successfully`,
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: `Error deleting ${this.deleteForm.attachment_url}`,
            },
          });
        });
    },
    deleteRadiology(radiology) {
      this.deleteForm = radiology;
      console.log("deleteRadiology", this.deleteForm);
      this.$bvModal.show("radiology-delete");
    },
    deleteRadiologyItem() {
      this.$store
        .dispatch("delete", `/patientCardRadiology/${this.deleteForm.id}`)
        .then(() => {
          this.getPatientRadiology();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: "deleted radiology successfully",
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: "Error deleting radiology",
            },
          });
        });
    },
    openUploadModal(radiology) {
      this.attachmentForm.attachable_id = radiology.id;
      this.attachmentForm.attachable_type = "radiology";
      this.$bvModal.show("modal-upload-attachment");
    },
    previewFile(event) {
      const file = event.target.files[0];
      if (file) {
        this.isImage = file.type.startsWith("image/");
        const reader = new FileReader();
        reader.onload = (e) => {
          this.filePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async submitAttachment() {
      this.loading = true;
      if (!this.selectedFile) return;

      const formData = new FormData();
      formData.append("attachment", this.selectedFile);
      formData.append("attachable_id", this.attachmentForm.attachable_id);
      formData.append("attachable_type", this.attachmentForm.attachable_type);

      try {
        const response = await axios.post("/attachment", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        console.log("File uploaded successfully", response.data);
        this.$bvModal.hide("modal-upload-attachment");
        this.selectedFile = null;
        this.filePreview = null;
        this.loading = false;
        this.getPatientRadiology();
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Upload Success",
            icon: "success",
            variant: "info",
            text: "File uploaded successfully",
          },
        });
      } catch (error) {
        console.error("Error uploading file", error);
        this.loading = false;
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Upload Error",
            icon: "error",
            variant: "danger",
            text: "Error uploading file",
          },
        });
      }
    },
    resetUploadState() {
      this.selectedFile = null;
      this.filePreview = null;
      this.isImage = false;
    },
  },
};
</script>

<style lang="scss" scoped>
// Patient Information Card Styles
.patient-info-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
}

.patient-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.patient-avatar {
  .b-avatar {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
}

.patient-name {
  font-weight: 700;
  color: #2c3e50;
  font-size: 1.5rem;
}

.patient-id {
  font-size: 0.9rem;
}

.status-badge {
  font-size: 0.8rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
}

.patient-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 1rem;
}

.detail-section {
  &.full-width {
    grid-column: 1 / -1;
  }
}

.section-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  font-weight: 500;
  color: #6c757d;
  min-width: 120px;
}

.detail-value {
  font-weight: 400;
  color: #2c3e50;
  text-align: right;
  flex: 1;
}

.condition-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.condition-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.condition-label {
  font-weight: 500;
  color: #495057;
}

.medical-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.medical-item {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.medical-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.medical-value {
  color: #2c3e50;
  line-height: 1.4;
}

// Radiology Attachments Styles
.radiology-attachments-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
}

.radiology-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.radiology-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.75rem;
  border-radius: 12px;
  margin-right: 1rem;
}

.radiology-title {
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.radiology-subtitle {
  font-size: 0.9rem;
}

.add-radiology-btn {
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
}

.radiology-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.radiology-item-card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
}

.radiology-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.radiology-item-info {
  flex: 1;
}

.radiology-item-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.radiology-item-count {
  font-size: 0.85rem;
  color: #6c757d;
  background: #f8f9fa;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
}

.radiology-item-actions {
  display: flex;
  gap: 0.5rem;
}

.attachments-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.attachment-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.attachment-preview {
  position: relative;
  width: 100%;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
  margin-bottom: 0.75rem;
}

.attachment-image-container {
  position: relative;
  width: 100%;
  height: 100%;

  .attachment-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .attachment-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;

    .overlay-icon {
      color: white;
      font-size: 1.5rem;
    }
  }

  &:hover .attachment-overlay {
    opacity: 1;
  }
}

.attachment-pdf-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #dc3545;

  .pdf-icon {
    margin-bottom: 0.5rem;
  }

  .pdf-label {
    font-size: 0.8rem;
    font-weight: 500;
  }
}

.attachment-actions {
  display: flex;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
}

.no-attachments {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.no-attachments-content {
  .no-attachments-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }

  .no-attachments-text {
    margin: 0;
    font-size: 0.9rem;
  }
}

.empty-radiology-state {
  text-align: center;
  padding: 4rem 2rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #dee2e6;
}

.empty-state-content {
  .empty-state-icon {
    color: #6c757d;
    margin-bottom: 1rem;
  }

  .empty-state-title {
    color: #495057;
    margin-bottom: 0.5rem;
  }

  .empty-state-description {
    color: #6c757d;
    margin-bottom: 2rem;
  }

  .empty-state-action {
    border-radius: 8px;
    padding: 0.75rem 2rem;
    font-weight: 600;
  }
}

// Upload Modal Styles
.upload-section {
  .upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    background: #f8f9fa;
    transition: all 0.3s ease;

    &:hover {
      border-color: #007bff;
      background: #e3f2fd;
    }
  }

  .upload-input {
    width: 100%;
  }
}

.preview-section {
  .preview-title {
    font-weight: 600;
    color: #495057;
    margin-bottom: 1rem;
  }

  .preview-container {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
    background: white;
  }

  .preview-image {
    width: 100%;
    max-height: 300px;
    object-fit: contain;
    display: block;
  }

  .preview-pdf {
    height: 400px;

    .preview-embed {
      width: 100%;
      height: 100%;
      border: none;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .patient-details-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .radiology-grid {
    grid-template-columns: 1fr;
  }

  .radiology-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .attachments-container {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
